import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';

import Button from '../Button';
import { IMAGE_SIZE_CODE } from '../../../constants/image-size-codes';
import { getAsset } from '../../../util/assets';
import { AVATAR_SIZE_52 } from '../../../constants/avatar-sizes';
import Avatar from '../Avatar';
import useCircles from '../../../apps/pro/views/Account/ContactCircles/hooks/useCircles';
import Spinner from '../Spinner';
import { getErrorString } from '../../../util/normalize-errors';
import Alert from '../Alert';
import { referralSignupInvitationsSend } from '../../../graphql/mutations/account';

const propTypes = {
  circleId: PropTypes.string.isRequired,
  sendInvitations: PropTypes.func.isRequired,
  onRequestClose: PropTypes.func.isRequired,
};
const ContactCircleShare = ({ circleId, sendInvitations, onRequestClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const { loading: loadingCircle, circle } = useCircles({
    id: circleId,
  });

  if (loadingCircle) {
    return (
      <Fragment>
        <div className="d-flex align-items-center justify-content-center">
          <Spinner data-testid="spinner" />
        </div>
      </Fragment>
    );
  }

  // If we stop loading and no circle has been found, just close the modal.
  if (!loadingCircle && !circle) {
    onRequestClose();
  }

  const onInviteCircle = async () => {
    setLoading(true);
    setError('');

    try {
      await sendInvitations(circle.id);
      setSuccess(true);
    } catch (e) {
      setError(getErrorString(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      {success ? (
        <Fragment>
          <img
            src={getAsset('/confetti-transparent.png')}
            alt="Confetti"
            className="confetti-backdrop"
          />
          <div className="d-flex align-items-center justify-content-center mb-2">
            <img
              src={getAsset('/waldo-excited.png')}
              width="48px"
              alt="Waldo Icon"
            />
          </div>
          <h4 className="text-center">Your invite has been sent!</h4>
          <p className="text-center text-dark">
            Your contacts will soon receive their invitation to join Waldo!
          </p>
          <Button
            btnStyle="link"
            className="mt-2 text-center"
            btnBlock
            onClick={onRequestClose}
          >
            Dismiss
          </Button>
        </Fragment>
      ) : (
        <Fragment>
          <div className="d-flex align-items-center justify-content-center mb-2">
            <Avatar
              photo={
                circle?.asset_image
                  ? {
                      id: circle.asset_image.id,
                      thumbnail_urls: [
                        {
                          size_code: IMAGE_SIZE_CODE.SMALL,
                          url: circle.asset_image.url,
                          width: 1,
                          height: 1,
                        },
                      ],
                    }
                  : {
                      thumbnail_urls: [
                        {
                          url: getAsset('/waldo-icon.svg'),
                          width: 0,
                          height: 0,
                        },
                      ],
                    }
              }
              size={AVATAR_SIZE_52}
              className={
                circle?.asset_image ? '' : 'contact-circle-tile-avatar'
              }
            />
          </div>

          <h4 className="text-center">Invite {circle?.name} to Waldo!</h4>
          <p className="text-center text-dark">
            Invite your friend &amp; family to Waldo&apos;s <br />
            seamless photo &amp; video sharing below!
          </p>

          {error && (
            <Alert
              type="danger"
              dismissible
              onDismiss={() => setError('')}
              className="w-100 my-2"
            >
              {error}
            </Alert>
          )}

          <div className="d-flex flex-column-reverse flex-md-row">
            <Button
              btnBlock
              btnOutline
              onClick={onRequestClose}
              className="mt-2 mt-md-0 mr-md-1"
              disabled={loading}
            >
              Maybe Later
            </Button>
            <Button
              btnBlock
              className="ml-md-1 mt-2 mt-md-0"
              onClick={onInviteCircle}
              disabled={loading}
            >
              {loading ? 'Sending invitations...' : 'Invite to Waldo'}
            </Button>
          </div>
        </Fragment>
      )}
    </Fragment>
  );
};

ContactCircleShare.propTypes = propTypes;

const withReferralSignupInvitationSend = graphql(
  referralSignupInvitationsSend,
  {
    props: ({ mutate }) => ({
      sendInvitations: circleId =>
        mutate({
          variables: {
            input: {
              circle_id: circleId,
            },
          },
        }),
    }),
  },
);

export { ContactCircleShare as ContactCircleShareComponent };

export default compose(withReferralSignupInvitationSend)(ContactCircleShare);
